/**
 * Client-side pre-check for BR-LL-01 (unit uniqueness). Pure so it can be unit-tested.
 * Mirrors the API's validateUnitConflict: a live lease only blocks a new one if it is still
 * running on the new lease's start date ("future lease scheduling" — a renewal may start the
 * day the old lease ends).
 */
import type { LeaseListItem } from '../../../types/lease';

type ConflictCandidate = Pick<LeaseListItem, 'state' | 'endDate' | 'actualEndDate'> & {
  archivedAt: string | null;
};

/** `ending` still occupies the unit (BR-LL-01/BR-LL-13), so it blocks like `active`/`scheduled`. */
const OCCUPYING_STATES = ['active', 'scheduled', 'ending'];

/** Date-only (YYYY-MM-DD) strings compare correctly as text; ISO timestamps are truncated first. */
const dateOnly = (value: string) => value.slice(0, 10);

export function findUnitConflict<T extends ConflictCandidate>(leases: T[], newStartDate: string): T | undefined {
  return leases.find(
    (lease) =>
      lease.archivedAt === null &&
      OCCUPYING_STATES.includes(lease.state) &&
      [lease.endDate, lease.actualEndDate].every((end) => end === null || dateOnly(end) > newStartDate)
  );
}
